import * as _ from 'lodash';
import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CoursesService } from 'src/app/services/courses.service';
import { IAdminCoursesQueryParams } from './admin-courses.viewmodel';
import { DatatablesHelper, RouteHelper, PromiseHelper } from 'src/app/helpers';
import { AdminService } from 'src/app/services/admin.service';
import { Subject } from 'rxjs';
import { DataTableDirective } from 'angular-datatables';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { FlashMessagesService } from 'flash-messages-angular';
import { EditCourseModuleDialogComponent } from './edit-course-module-dialog/edit-course-module-dialog.component';
import { EditCourseModuleDialogData, EditCourseModuleDialogForm } from './edit-course-module-dialog/edit-course-module-dialog.viewmodel';
import { Course, CourseModule, CourseModulePage } from 'src/app/models';
import { AppContextService, RouteService } from 'src/app/core/services';
import { SafeSubscribable } from 'src/app/core/rxjs/extensions';

@Component({
  selector: 'app-admin-courses',
  templateUrl: './admin-courses.component.html',
  styleUrls: ['./admin-courses.component.css']
})
export class AdminCoursesComponent implements OnInit, OnDestroy, SafeSubscribable {

  // List of courses
  courses: any[] = [];
  coursesLoaded = true; // by resolver during routing (see app-routing.module)

  modules: CourseModule[] = [];
  modulesLoaded = false;

  pages: CourseModulePage[] = [];
  pagesLoaded = false;

  courseID: string = '';
  course?: Course|null;

  moduleID: string = '';
  module?: CourseModule|null;

  pageID: string = '';
  page?: CourseModulePage|null;

  routeParams: IAdminCoursesQueryParams = {};

  // Needed  this to display option letters
  letterArray: String[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

  dtOptionsModules: DataTables.Settings = {};

  // We use this trigger because fetching the list of users can take a while,
  // thus we ensure the data is fetched before rendering
  dtTriggerModules: Subject<any> = new Subject<any>();

  @ViewChild('modulesTable', { static: false })
  dtElementModules?: DataTableDirective;

  dtOptionsPages: DataTables.Settings = {};

  // We use this trigger because fetching the list of users can take a while,
  // thus we ensure the data is fetched before rendering
  dtTriggerPages: Subject<any> = new Subject<any>();

  @ViewChild('pagesTable', { static: false })
  dtElementPages?: DataTableDirective;

  constructor(
    private coursesService: CoursesService,
    private adminService: AdminService,
    private router: Router,
    private route: ActivatedRoute,
    private routeService: RouteService,
    private dialog: MatDialog,
    private flashMessage: FlashMessagesService,
    private appContext: AppContextService
    ) { }

  ngOnInit(): void {
    this.initTables();
    // this.loadRouteParams();
    // this.loadData();

    this.route.queryParams.safeSubscribe(this, () => {
      if (this.coursesService.courses.length === 0) {
        this.resetData();
      }

      this.loadRouteParams();
      this.loadData();
    });
  }

  ngOnDestroy(): void {}

  private initTables(): void {
    this.initModulesTable();
    this.initPagesTable();
  }

  private initModulesTable(): void {
    this.dtOptionsModules = {
      pagingType: 'full_numbers',
      pageLength: 50,
      // enables horizontal scrolling on datatable
      scrollX: true,

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      dom: 'lBfrtip',
    };

    // Configure the buttons
    Object.assign(this.dtOptionsModules, {
      buttons: [
        'excel'
      ]
    });
  }

  private initPagesTable(): void {
    this.dtOptionsPages = {
      pagingType: 'full_numbers',
      pageLength: 50,
      // enables horizontal scrolling on datatable
      scrollX: true,

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      dom: 'lBfrtip',
    };

    // Configure the buttons
    Object.assign(this.dtOptionsPages, {
      buttons: [
        'excel'
      ]
    });
  }

  private resetData(): void {
    this.coursesLoaded = false;
    this.modulesLoaded = false;
    this.pagesLoaded = false;
  }

  private loadRouteParams(): void {
    const queryParams = this.route.snapshot.queryParams as IAdminCoursesQueryParams;

    this.routeParams = RouteHelper.map(queryParams, {
      courseid: String,
      moduleid: String,
      pageid:   String,
    });

    this.courseID = this.routeParams.courseid || '';
    this.moduleID = this.routeParams.moduleid || '';
    this.pageID   = this.routeParams.pageid || '';
  }

  private async loadData() {
    await this.loadCourses();
    await this.loadModules();
    await this.loadPages();
  }

  private async loadCourses() {
    if (!this.coursesLoaded) {
      const res = await this.coursesService.getAllCourses({ getmodules: true }).toPromise();
      this.coursesService.courses = res.courses;
      this.coursesService.currentCourse = this.coursesService.currentCourse
        ? this.coursesService.courses.find(course => course._id === this.coursesService.currentCourse._id)
        : null;
      this.coursesLoaded = true;
    }

    this.courses = this.coursesService.courses;
    this.course  = this.courseID ? this.courses.find(c => c.title === this.courseID) : null;
  }

  private async loadModules() {
    if (!this.coursesLoaded) {
      await this.loadCourses();
    }

    this.modules = this.course?.modules || [];
    this.module  = this.modules.length && this.moduleID ? this.modules.find(m => m.moduleID === this.moduleID) : null;

    if (this.courseID && !this.moduleID) {
      this.loadModulesTable();
    }
  }

  private async loadPages() {
    if (!this.coursesLoaded) {
      await this.loadCourses();
      await this.loadModules();
    }

    this.pages = this.module?.pages || [];
    this.page  = this.pages.length && this.pageID ? this.pages.find(p => p.pageID === this.pageID) : null;

    if (this.courseID && this.moduleID) {
      this.loadPagesTable();
    }
  }

  private async tryReloadModules() {
    this.courses = [];
    this.coursesLoaded = false;
    await this.loadModules().catch(() => {});
  }

  private async tryReloadPages() {
    this.courses = [];
    this.coursesLoaded = false;
    await this.loadPages().catch(() => {});
  }

  private async loadModulesTable() {
    if (!this.dtElementModules) {
      await PromiseHelper.delay(1);
    }

    if (!this.dtElementModules) {
      return;
    }

    if (this.modulesLoaded) {
      this.rerenderModulesTable();
    }
    else {
      // Calling the DT trigger to manually render the table
      this.dtTriggerModules.next();
      this.modulesLoaded = true;
    }
  }

  private async loadPagesTable() {
    if (!this.dtElementPages) {
      await PromiseHelper.delay(1);
    }

    if (!this.dtElementPages) {
      return;
    }

    if (this.pagesLoaded) {
      this.rerenderPagesTable();
    }
    else {
      // Calling the DT trigger to manually render the table
      this.dtTriggerPages.next();
      this.pagesLoaded = true;
    }
  }

  private rerenderModulesTable() {
    if (this.dtElementModules?.dtInstance) {
      DatatablesHelper.redrawTable(this.dtElementModules, this.dtTriggerModules);
    }
  }

  private rerenderPagesTable() {
    if (this.dtElementPages?.dtInstance) {
      DatatablesHelper.redrawTable(this.dtElementPages, this.dtTriggerPages);
    }
  }

  private resetModulesTable() {
    this.dtTriggerModules = new Subject();
    this.modulesLoaded = false;
  }

  private resetPagesTable() {
    this.dtTriggerPages = new Subject();
    this.pagesLoaded = false;
  }

  private refreshModulesTable() {
    if (this.course && !this.moduleID) {
      this.loadModulesTable();
    }
    else if (this.modulesLoaded) {
      this.resetModulesTable();
    }
  }

  private refreshPagesTable() {
    if (this.course && this.module) {
      this.loadPagesTable();
    }
    else if (this.pagesLoaded) {
      this.resetPagesTable();
    }
  }

  // Navigate to the course clicked on
  onCourse(course: any) {
    this.course = course;
    this.courseID = course.title;
    this.coursesService.currentCourse = course;
    this.open(this.courseID, '');
    this.loadModules();
  }

  // Navigate to the module clicked on
  onCourseModule(module: any) {
    this.module = module;
    this.moduleID = module.moduleID;
    this.coursesService.currentModule = module;
    this.open(this.courseID, this.moduleID);
    this.loadPages();
  }

  goBack() {
    // open modules
    if (this.courseID && this.moduleID) {
      this.open(this.courseID, '');
    }
    // open courses
    else if (this.courseID && !this.moduleID) {
      this.open('', '');
    }
  }

  async open(courseID: string, moduleID: string) {
    this.courseID = courseID;
    this.moduleID = moduleID;

    if (!courseID) {
      this.course = null;
    }

    if (!moduleID) {
      this.module = null;
    }

    const queryParams = {
      courseid: courseID || null,
      moduleid: moduleID || null,
    };

    await this.router.navigate(['.'], { queryParams, relativeTo: this.route });

    this.refreshModulesTable();
    this.refreshPagesTable();

    this.routeService.scrollToTop();
  }

  courseName(course: any) {
    return this.coursesService.getName(course);
  }

  openModule(index: number) {
    const item = this.course?.modules[index];
    this.onCourseModule(item);
  }

  editModule(index: number) {
    const cModule = this.modules[index];
    const dialogConfig = new MatDialogConfig();

    const data: EditCourseModuleDialogData = {
      title: 'Edit Module',

      // Gotta send all the module data to the dialog to prefill
      moduleTitle: cModule.title,
    };

    dialogConfig.data = data;
    dialogConfig.autoFocus = true;

    // Open dialog
    const dialogRef = this.dialog.open(EditCourseModuleDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then((data: EditCourseModuleDialogForm) => {
      if (!data) return;

      this.routeService.scrollToTop();

      const body = {
        title: data.moduleTitle,
      };

      // Update Module
      this.adminService.updateCourseModule(this.courseID, cModule.moduleID, body).toPromise().then(async (data: any) => {

        // Rerender manually
        await this.tryReloadModules();

        // Success
        if (data.success) {
          // Display success
          this.flashMessage.show(data.message || "Module updated.", {
            cssClass: 'alert-success',
            timeout: 3000
          });
        }
      });
    });
  }

  async deleteModule(index: number) {
    const cModule = this.modules[index];
    const pages   = cModule.pages?.length || 0;

    if (pages > 0) {
      const warning = `${pages} page(s) will be removed as well.`;
      const message = `Are you sure you want to permanently delete module${cModule.title ? ' "' + cModule.title + '"' : ''}? ${warning}`;

      if (confirm(message) === false) {
        return;
      }
    }

    // Delete Module
    await this.adminService.deleteCourseModule(this.courseID, cModule.moduleID, { force: true }).toPromise();

    // Reload table data
    await this.tryReloadModules();
  }

  previewPage(event: Event, index: number) {
    this.appContext.update({ fastForward: true });
  }

  async deletePage(index: number) {
    const page = this.pages[index];

    if (confirm(`Are you sure you want to permanently delete page${page.title ? ' "' + page.title + '"' : ''}?`) === false) {
      return;
    }

    // Delete Page
    await this.adminService.deleteCoursePage(this.courseID, this.moduleID, page.pageID).toPromise();

    // Reload table data
    await this.tryReloadPages();
  }

  addModuleDialog() {
    const dialogConfig = new MatDialogConfig();

    const data: EditCourseModuleDialogData = {
      title: 'Add Module',
    };

    dialogConfig.autoFocus = true;
    dialogConfig.data = data;

    const dialogRef = this.dialog.open(EditCourseModuleDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then(async (data: EditCourseModuleDialogForm) => {
      if (!data) return;

      const body = {
        title: data.moduleTitle,
      };

      // Add Modue
      const res: any = await this.adminService.addCourseModule(this.courseID, body).toPromise();

      // Rerender manually
      await this.tryReloadModules();

      // Success
      if (res.success) {
        // Display success
        this.flashMessage.show(res.message || "Module added.", {
          cssClass: 'alert-success',
          timeout: 3000
        });
      }
    });
  }

  moveModuleUp(index: number) {
    if (index <= 0) return;
    this.moveModule(index, -1);
  }

  moveModuleDown(index: number) {
    if (index >= this.modules.length - 1) return;
    this.moveModule(index, 1);
  }

  private async moveModule(index: number, increment: number) {
    if (!increment) return;

    const array     = this.modules;
    const item      = array[index];
    const target    = array[index + increment];

    if (!item || !target) {
      return;
    }

    const newIndex = this.moveIn(array, index, index + increment);

    if (newIndex >= 0) {
      try {
        const data = increment > 0 ? { moveAfterModuleID: target.moduleID } : { moveBeforeModuleID: target.moduleID };
        const res: any = await this.adminService.updateCourseModule(this.courseID, item.moduleID, data).toPromise();

        if (res.success) {
          this.updateModulesOrder();
        }
      }
      catch (err) {
        console.log(err);

        this.flashMessage.show("Failed to move module, please refresh the page and try again.", {
          cssClass: 'alert-danger',
          timeout: 5000
        });
      }
    }
  }

  movePageUp(index: number) {
    if (index <= 0) return;
    this.movePage(index, -1);
  }

  movePageDown(index: number) {
    if (index >= this.pages.length - 1) return;
    this.movePage(index, 1);
  }

  private async movePage(index: number, increment: number) {
    if (!this.module || !increment) return;

    const array     = this.module?.pages || [];
    const item      = array[index];
    const target    = array[index + increment];

    if (!item || !target) {
      return;
    }

    const newIndex = this.moveIn(array, index, index + increment);

    if (newIndex >= 0) {
      try {
        const data = increment > 0 ? { moveAfterPageID: target.pageID } : { moveBeforePageID: target.pageID };
        const res: any = await this.adminService.updateCoursePage(this.courseID, this.moduleID, item.pageID, data).toPromise();

        if (res.success) {
          this.updatePagesOrder(this.module);
        }
      }
      catch (err) {
        console.log(err);

        this.flashMessage.show("Failed to move item, please refresh the page and try again.", {
          cssClass: 'alert-danger',
          timeout: 5000
        });
      }
    }
  }

  private moveIn(arr: any[], fromIndex: number, toIndex: number) {
    if (fromIndex < 0 || fromIndex >= arr.length) return -1;  // out of range
    toIndex = Math.max(0, Math.min(toIndex, arr.length - 1)); // clamp index in range
    arr.splice(toIndex, 0, arr.splice(fromIndex, 1)[0]);      // cut and paste
    return toIndex;
  }

  private updateModulesOrder() {
    this.modules.forEach((cModule, index) => {
      cModule.moduleNumber = index + 1;
    });
  }

  private updatePagesOrder(module: CourseModule) {
    module.pages.forEach((page, index) => {
      page.pageNumber = index + 1;
    });
  }
}
